import { getSourceRootLabel } from './source-root'
import type { FileSearchResult, FolderPathEntry, FolderSearchResult } from './schemas'

export type DriveSearchRow =
  | { kind: 'folder'; data: FolderSearchResult; locationLabel: string; locationPath: string }
  | { kind: 'file'; data: FileSearchResult; locationLabel: string; locationPath: string }

/**
 * The whole trail, shown on hover over the Location cell because the cell itself only has room for
 * the immediate parent. Rooted at the drive the item lives in when the matter has one connected,
 * so a synced `Bills` reads differently from our own.
 */
export function buildLocationPath(folderPath: readonly FolderPathEntry[], rootLabel: string | null, matterName: string): string {
  const segments = [...(rootLabel ? [rootLabel] : []), ...folderPath.map((entry) => entry.name)]

  return segments.length > 0 ? segments.join('/') : matterName
}

export type BuildSearchRowsParams = {
  folders: FolderSearchResult[]
  files: FileSearchResult[]
  /** Rendered as the Location of an item that sits in the matter root with no drive connected. */
  matterName: string
  /** Whether this matter has a drive connected, which is when the source root is worth naming. */
  hasSyncedDrive: boolean
}

export type BuildSearchRowsResult = {
  /** Folders first, then files — each group in the order the backend ranked it. */
  rows: DriveSearchRow[]
  /** Ancestor folder ids per result id, for `pruneCoveredSelection`. */
  ancestorFolderIdsById: Map<string, string[]>
}

/**
 * Merge the two search responses into one result list.
 *
 * `folder_path` excludes the matter root (and, for a folder, itself), so its last entry is the
 * item's immediate parent — the Location the design shows. An empty array means the item lives in
 * the matter root, which renders as the matter name rather than a blank cell.
 */
export function buildSearchRows({ folders, files, matterName, hasSyncedDrive }: BuildSearchRowsParams): BuildSearchRowsResult {
  const ancestorFolderIdsById = new Map<string, string[]>()

  const toRow = <T extends FolderSearchResult | FileSearchResult>(data: T): { data: T; locationLabel: string; locationPath: string } => {
    ancestorFolderIdsById.set(
      data.id,
      data.folder_path.map((entry) => entry.id)
    )
    const rootLabel = getSourceRootLabel(data, hasSyncedDrive)
    const parent = data.folder_path.at(-1)

    // An item sitting at a drive's root has no parent folder to name, so the drive itself is the
    // most specific answer available — and more useful than the matter name, which is a given.
    return { data, locationLabel: parent?.name ?? rootLabel ?? matterName, locationPath: buildLocationPath(data.folder_path, rootLabel, matterName) }
  }

  const rows: DriveSearchRow[] = [
    ...folders.map((folder) => ({ kind: 'folder' as const, ...toRow(folder) })),
    ...files.map((file) => ({ kind: 'file' as const, ...toRow(file) })),
  ]

  return { rows, ancestorFolderIdsById }
}
